import { defineStore } from 'pinia';
import { useSnackbarStore } from './snackbar';
import type { Organization } from '~/models/organization';
import type { AdminPermission } from '~/models/admin-role';
import { profileService } from '~/services/profile.service';

export const adminTokenCookieName = 'admin_access_token';
export const refreshTokenCookieName = 'admin_refresh_token';
export const tokenExpiryTimeCookieName = 'admin_token_expiry';
export const currentShopIdCookieName = 'current_shop_id';
export const currentOrganizationIdCookieName = 'current_organization_id';
const PREFERENCE_MAX_AGE_SEC = 60 * 60 * 24 * 365;
/** Matches AuthController::REFRESH_TOKEN_TTL_DAYS (pos-backend). */
const REFRESH_TOKEN_MAX_AGE_SEC = 60 * 60 * 24 * 30;
/**
 * Each refresh also issues a new refresh token. A refresh one day before the access token
 * expires keeps the sign-in alive on a visit, and no request fails with a 401 during use.
 */
const TOKEN_REFRESH_MARGIN_MS = 24 * 60 * 60 * 1000;

let ongoingRefresh: Promise<boolean> | null = null;

/**
 * Read shop/org preference without useCookie.
 * Pinia getters must not call useCookie — each call registers watchers/onScopeDispose.
 */
function readPreference(name: string): string | null {
  if (!import.meta.client) return null;

  try {
    const fromStorage = localStorage.getItem(name);
    if (fromStorage) return fromStorage;
  } catch {
    // ignore
  }

  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  if (!match?.[1]) return null;

  const fromCookie = decodeURIComponent(match[1]);
  try {
    localStorage.setItem(name, fromCookie);
  } catch {
    // ignore
  }
  return fromCookie;
}

/**
 * Persist preference synchronously before hard reload.
 * useCookie + window.location.reload() races: the cookie watcher often never flushes,
 * so the previous shop (e.g. L'Effervescence) comes back after reload.
 */
function writePreference(name: string, value: string | null): void {
  if (!import.meta.client) return;

  try {
    if (value == null) localStorage.removeItem(name);
    else localStorage.setItem(name, value);
  } catch {
    // ignore
  }

  if (value == null) {
    document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
    return;
  }

  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${PREFERENCE_MAX_AGE_SEC}; SameSite=Lax${secure}`;
}

/**
 * Write a token cookie synchronously. useCookie writes in a watcher, so a request sent
 * right after a refresh can read the old token from document.cookie.
 */
function writeTokenCookie(name: string, value: string | null, maxAgeSec = 0): void {
  if (!import.meta.client) return;

  const maxAge = value == null ? 0 : maxAgeSec;
  document.cookie = `${name}=${encodeURIComponent(value ?? '')}; Path=/; Max-Age=${maxAge}; SameSite=Strict; Secure`;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

interface AuthStoreState {
  authenticated: boolean;
  operator: Operator | null;
  shops: Shop[];
  organizations: Organization[];
  square_payment_brands: string[];
  permissions: AdminPermission[];
}

const selectCurrentOrganization = (state: AuthStoreState): Organization | null => {
  if (!state.operator) {
    return null;
  }

  const raw = readPreference(currentOrganizationIdCookieName);
  const orgId = raw ? Number(raw) : state.operator.organization_id;
  const organization = state.organizations.find(organization => organization.id === orgId);
  return organization || state.organizations[0] || null;
};

const selectCurrentShop = (state: AuthStoreState): Shop | null => {
  if (!state.operator) {
    return null;
  }

  const raw = readPreference(currentShopIdCookieName);
  const shopId = raw ? Number(raw) : 0;
  const shop = Number.isFinite(shopId) ? state.shops.find(shop => shop.id === shopId) : undefined;
  return shop || state.shops[0] || null;
};

export const useAuthStore = defineStore('auth', {
  state: () =>
    ({
      authenticated: false,
      operator: null,
      shops: [],
      organizations: [],
      square_payment_brands: [],
      permissions: [],
    }) as AuthStoreState,

  getters: {
    currentShop: selectCurrentShop,
    currentOrganization: selectCurrentOrganization,
  },

  actions: {
    async login(email: string, password: string): Promise<boolean> {
      const { result, error } = await useMyFetch<LoginResponse>('/api/v1/admin/login', {
        method: 'POST',
        body: {
          email,
          password,
        },
      });

      if (!result || error) {
        const { showMessage } = useSnackbarStore();
        showMessage({ content: 'ログインに失敗しました', color: 'error' });
        return false;
      }

      this.storeTokens(result.access_token, result.refresh_token, result.expires_in);
      this.authenticated = true;
      return true;
    },

    /** The backend signs out every other session and returns new tokens for this one. */
    async changePassword(currentPassword: string, password: string): Promise<boolean> {
      const { result, error } = await useMyFetch<LoginResponse>('/api/v1/admin/password', {
        method: 'PUT',
        body: {
          current_password: currentPassword,
          password,
        },
      });

      if (!result || error) {
        return false;
      }

      this.storeTokens(result.access_token, result.refresh_token, result.expires_in);
      return true;
    },

    storeTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
      // A cookie without Max-Age is a session cookie. The browser deletes it on close,
      // so the sign-in does not survive a browser restart.
      writeTokenCookie(adminTokenCookieName, accessToken, expiresIn);
      writeTokenCookie(refreshTokenCookieName, refreshToken, REFRESH_TOKEN_MAX_AGE_SEC);
      writeTokenCookie(tokenExpiryTimeCookieName, String(Date.now() + expiresIn * 1000), expiresIn);
    },

    /** Calls that arrive during a refresh share its result. */
    refreshTokens(): Promise<boolean> {
      ongoingRefresh ??= this.requestNewTokens().finally(() => {
        ongoingRefresh = null;
      });
      return ongoingRefresh;
    },

    async requestNewTokens(): Promise<boolean> {
      const refreshToken = useCookie(refreshTokenCookieName).value;
      if (!refreshToken) {
        return false;
      }

      const { result, error } = await useMyFetch<RefreshResponse>('/api/v1/admin/refresh', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
        skipDefaultErrorHandling: true,
      });

      if (!result || error) {
        return false;
      }

      this.storeTokens(result.access_token, result.refresh_token, result.expires_in);
      this.authenticated = true;
      return true;
    },

    /** True when a refresh token exists and the access token is missing or expires within the margin. */
    shouldRefreshTokens(): boolean {
      if (!useCookie(refreshTokenCookieName).value) return false;
      if (!useCookie(adminTokenCookieName).value) return true;

      const expiryTime = Number(useCookie(tokenExpiryTimeCookieName).value);
      return !expiryTime || expiryTime - Date.now() < TOKEN_REFRESH_MARGIN_MS;
    },

    clearTokens(): void {
      writeTokenCookie(adminTokenCookieName, null);
      writeTokenCookie(refreshTokenCookieName, null);
      writeTokenCookie(tokenExpiryTimeCookieName, null);
    },

    async logout(): Promise<void> {
      const token = useCookie(adminTokenCookieName);
      if (token.value) {
        await useMyFetch('/api/v1/admin/logout', {
          method: 'POST',
        });
      }

      await this.resetAndGoToSignin();
    },

    async refreshProfile(force: boolean = false): Promise<void> {
      // Cache profile data in memory to avoid unnecessary API calls
      if (this.operator && !force) {
        return;
      }

      const rawOrgId = readPreference(currentOrganizationIdCookieName);
      const orgId = rawOrgId ? Number(rawOrgId) : undefined;

      const { result, error } = await profileService.fetchProfile({
        organization_id: orgId,
      });

      if (!result || error) {
        const { showMessage } = useSnackbarStore();
        showMessage({ content: 'プロフィールの取得に失敗しました', color: 'error' });
        return;
      }

      this.operator = result.data.operator;
      this.shops = result.data.shops;
      this.organizations = result.data.organizations;
      this.square_payment_brands = result.data.square_payment_brands ?? [];
      this.permissions = result.data.permissions ?? [];
    },

    /**
     * Changes the current shop ID and reloads the page to apply the change.
     * If no shop ID is provided, it clears the current shop ID.
     * @param shopId
     */
    changeCurrentShopId(shopId?: number): void {
      writePreference(currentShopIdCookieName, shopId != null ? String(shopId) : null);

      window.location.reload();
    },

    async changeCurrentOrganizationId(organizationId: number): Promise<void> {
      writePreference(currentOrganizationIdCookieName, String(organizationId));

      // Refresh profile to update operator and shops
      await this.refreshProfile(true);

      // Auto-select the first shop if available
      this.changeCurrentShopId(this.shops[0]?.id);
    },

    async resetAndGoToSignin(): Promise<void> {
      this.clearTokens();
      writePreference(currentShopIdCookieName, null);
      writePreference(currentOrganizationIdCookieName, null);

      // Hard reload to prevent any cached state from being used
      await navigateTo('/signin', { external: true });
    },

    /** True when the operator holds any one of the keys. */
    hasPermission(permission: AdminPermission | AdminPermission[]): boolean {
      const required = Array.isArray(permission) ? permission : [permission];
      return required.some(key => this.permissions.includes(key));
    },

    canOpenPage(path: string): boolean {
      const pagePermission = pagePermissionFor(path);
      return !pagePermission || this.hasPermission(pagePermission.read);
    },

    /** Create and update actions. A page without a write key allows them. */
    canWritePage(path: string): boolean {
      const write = pagePermissionFor(path)?.write;
      return !write || this.hasPermission(write);
    },

    canDeleteOnPage(path: string): boolean {
      const pagePermission = pagePermissionFor(path);
      const key = pagePermission?.delete ?? pagePermission?.write;
      return !key || this.hasPermission(key);
    },

    landingPath(): string {
      return LANDING_PATHS.find(path => this.canOpenPage(path)) ?? '/announcements';
    },

    canUseFeature(featureKey: FeatureKey): boolean {
      if (!this.operator) {
        return false;
      }

      if (this.currentShop?.features.some(feature => feature.key === featureKey)) {
        return true;
      }

      if (this.currentOrganization?.features.some(feature => feature.key === featureKey)) {
        return true;
      }

      return false;
    },

    /** Feature flags for an arbitrary shop in the operator's list (e.g. report shop selector). */
    shopHasFeature(shopUid: string, featureKey: FeatureKey): boolean {
      if (!this.operator) {
        return false;
      }

      const shop = this.shops.find(s => s.uid === shopUid);
      if (shop?.features.some(feature => feature.key === featureKey)) {
        return true;
      }

      if (this.currentOrganization?.features.some(feature => feature.key === featureKey)) {
        return true;
      }

      return false;
    },
  },
});
